import {makeAutoObservable} from "mobx";
import {ViewController} from "data/types/structure";
import {injectable, inject} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IContestsStore} from "data/stores/contests/contestsStore";
import type {IRosterStore} from "data/stores/roster/roster.store";
import type {IModalsStore} from "data/stores/modals/modals.store";
import {DateTime} from "luxon";
import {GAME_TIMEZONE} from "data/constants";
import {ContestStatus, ModalType} from "data/enums";

export interface IH2hNavigationController extends ViewController {
	toPrevContest: () => void;
	toNextContest: () => void;
	showDayPicker: () => void;

	get i18n(): ILocalizationStore;

	get contestDay(): string;

	get isPrevDisabled(): boolean;

	get isNextDisabled(): boolean;
}

@injectable()
export class H2hNavigationController implements IH2hNavigationController {
	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.ContestsStore) private _contestsStore: IContestsStore,
		@inject(Bindings.RosterStore) private _rosterStore: IRosterStore,
		@inject(Bindings.ModalsStore) private _modalsStore: IModalsStore
	) {
		makeAutoObservable(this);
	}

	get contestDay(): string {
		const currentContest = this._contestsStore.currentContest;
		if (!currentContest) {
			return "-";
		}

		const contestDate = DateTime.fromISO(currentContest.date).setZone(GAME_TIMEZONE);
		return contestDate.isValid ? contestDate.toFormat("L/dd") : "-";
	}

	get isPrevDisabled(): boolean {
		return !this._rosterStore.prevContestId;
	}

	get isNextDisabled(): boolean {
		const currentContest = this._contestsStore.currentGameContest;
		const nextContestId = this._rosterStore.nextContestId ?? 0;
		const isDisabled =
			this._contestsStore.getContestById(nextContestId)?.status === ContestStatus.Scheduled;
		if (isDisabled) {
			return true;
		}
		if (nextContestId) {
			return false;
		}

		return !(currentContest && currentContest?.id !== this._contestsStore.currentContest?.id);
	}

	public toPrevContest = () => {
		const contestId = this._rosterStore.prevContestId;
		if (contestId) {
			this._contestsStore.toContestId(contestId);
		}
	};

	public toNextContest = () => {
		const currentContest = this._contestsStore.currentGameContest;
		const contestId = this._rosterStore.nextContestId;
		if (contestId) {
			this._contestsStore.toContestId(contestId);
		} else if (currentContest) {
			this._contestsStore.toContestId(currentContest.id);
		}
	};

	public showDayPicker = () => {
		this._modalsStore.showModal(ModalType.DAY_PICKER, {
			meta: {
				type: "picker",
				lockByContests: this.playedContests,
			},
		});
	};

	get playedContests() {
		const rosterPlayed = this._rosterStore.playedContests || [];
		const currentContest = this._contestsStore.currentGameContest;

		if (!currentContest) {
			return rosterPlayed;
		}

		return [...rosterPlayed, currentContest.id];
	}
}
